import {
  initialTicketMessages,
  initialVendorTickets,
} from "../../../mocks/ticketMock";
import { cacheTicketDescription } from "../../ticketUtils";

initialVendorTickets.forEach((ticket) => {
  if (ticket.description) {
    cacheTicketDescription(ticket.id, ticket.description);
  }
});

const MESSAGE_PAGE_SIZE = 10;

function cloneTickets() {
  return initialVendorTickets.map((t) => ({ ...t }));
}

function cloneMessages() {
  const out = {};
  for (const [ticketId, rows] of Object.entries(initialTicketMessages)) {
    out[ticketId] = rows.map((m) => ({ ...m }));
  }
  return out;
}

let tickets = cloneTickets();
let messagesByTicket = cloneMessages();

function resetMockState() {
  tickets = cloneTickets();
  messagesByTicket = cloneMessages();
}

function createMockUuid() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return "a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a99";
}

export const mockTicketProvider = {
  resetMockState,

  createTicket: async ({ title, description, file }) => {
    const ticketId = createMockUuid();
    const now = new Date().toISOString();
    tickets.unshift({
      id: ticketId,
      subject: title.trim(),
      state: "aperto",
      createdAt: now,
      updatedAt: now,
      unreadCount: 0,
      description: description.trim(),
    });
    cacheTicketDescription(ticketId, description.trim());
    messagesByTicket[ticketId] = [];
    if (file) {
      messagesByTicket[ticketId].push({
        id: `mock-msg-${Date.now()}`,
        operator: false,
        message: null,
        attachmentName: file.name,
        timestamp: now,
        editable: false,
      });
    }
    return { data: { ticketId } };
  },

  listTickets: async (payload) => {
    const { state, page = 0, size = 20 } = payload ?? {};
    let filtered = tickets;
    if (state) {
      filtered = tickets.filter((t) => t.state === state);
    }
    const slice = filtered.slice(page * size, page * size + size);
    return {
      data: {
        items: slice.map(({ description, ...item }) => item),
        totalCount: filtered.length,
        page,
        size,
      },
    };
  },

  sendMessage: async ({ ticketId, message, file }) => {
    const messageId = createMockUuid();
    const row = {
      id: messageId,
      operator: false,
      message: message?.trim() || null,
      attachmentName: file?.name ?? null,
      timestamp: new Date().toISOString(),
      editable: true,
    };
    if (!messagesByTicket[ticketId]) {
      messagesByTicket[ticketId] = [];
    }
    messagesByTicket[ticketId].unshift(row);
    return { data: { messageId } };
  },

  listMessages: async ({ ticketId, page = 0 }) => {
    const all = messagesByTicket[ticketId] ?? [];
    const slice = all.slice(page * MESSAGE_PAGE_SIZE, page * MESSAGE_PAGE_SIZE + MESSAGE_PAGE_SIZE);
    return {
      data: {
        items: slice,
        totalCount: all.length,
        page,
        size: MESSAGE_PAGE_SIZE,
      },
    };
  },

  downloadAttachment: async ({ ticketId, messageId }) => {
    const row = (messagesByTicket[ticketId] ?? []).find((m) => m.id === messageId);
    return {
      data: {
        attachmentName: row?.attachmentName ?? "allegato.pdf",
        attachmentBase64: "JVBERi0x",
      },
    };
  },

  editMessage: async ({ ticketId, messageId, action, message }) => {
    const rows = messagesByTicket[ticketId] ?? [];
    const idx = rows.findIndex((m) => m.id === messageId);
    if (idx < 0) return { data: { statusCode: 400 } };
    if (action === "delete") {
      rows.splice(idx, 1);
    } else {
      rows[idx] = { ...rows[idx], message: message?.trim() ?? "" };
    }
    return { data: { statusCode: 200, message: "Operazione correttamente eseguita" } };
  },

  getSummary: async () => {
    const unreadTickets = tickets.filter(
      (t) => (t.state === "in-lavorazione" || t.state === "chiuso") && (t.unreadCount ?? 0) > 0
    ).length;
    const pollingEnabled = tickets.some((t) => t.state === "aperto" || t.state === "in-lavorazione");
    const snapshots = tickets.map((t) => ({
      id: t.id,
      state: t.state,
      updatedAt: t.updatedAt,
      unreadCount: t.unreadCount ?? 0,
    }));
    return { data: { menuBadgeCount: unreadTickets, pollingEnabled, tickets: snapshots } };
  },

  pollMessages: async ({ ticketId, afterTimestamp, ownMessageIds = [] }) => {
    const all = messagesByTicket[ticketId] ?? [];
    const afterMs = afterTimestamp ? new Date(afterTimestamp).getTime() : 0;
    const items = all
      .filter((m) => {
        const ts = new Date(m.timestamp).getTime();
        return !Number.isNaN(ts) && ts > afterMs;
      })
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    const readMessageIds = all
      .filter((m) => !m.operator && m.read && ownMessageIds.includes(m.id))
      .map((m) => m.id);
    return { data: { items, readMessageIds } };
  },
};
